import {
  DeleteOutlined,
  PlusOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import {
  Button,
  Col,
  Collapse,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
} from 'antd'
import { SUB_DRA_MAPPINGS } from 'features/indicationTriage'
import { InspectionSummaryData } from 'pages/reporting/reports/raut/rautReportData'
import { useRautReportOps } from 'pages/reporting/reports/raut/RautReportProvider'
import {
  displayMean,
  displayMin,
  displayNominal,
  displayWallLoss,
} from 'pages/reporting/reports/raut/sections/subSections/indicationDetailsComponents/indicationDisplay/utils/valueDisplayHelpers'
import { TYPE_TO_SHORT_NAME } from 'pages/reporting/shared/constants'
import {
  FormSectionSubtitle,
  FormSectionTitle,
  GenerateButton,
  SecondaryButton,
  StyledForm,
} from 'pages/reporting/shared/StyledComponents'
import { getDRADescription } from 'pages/reporting/shared/utils/DRAUtils'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

/**
 * SummaryContainer - Main wrapper with logical sections
 */
const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`

/**
 * SectionDivider - Visual separator between major sections
 */
const SectionDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.forecast.cardDividerBackground};
  margin: 0.5rem 0;
`

/**
 * AreaCard - Styled card for individual area entries
 */
const AreaCard = styled.div`
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
  border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
  border-radius: 0.5rem;
  overflow: hidden;
  margin-bottom: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`

/**
 * AreaCardHeader - Header section of area card with title and delete button
 */
const AreaCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.625rem 0.875rem;
  background: ${({ theme }) => theme.forecast.cardGroupBackground};
  border-bottom: 1px solid
    ${({ theme }) => theme.forecast.cardDividerBackground};
`

/**
 * AreaCardBody - Content section of area card
 */
const AreaCardBody = styled.div`
  padding: 1rem;
  background: ${({ theme }) => theme.forecast.cardBackground};
`

/**
 * AreaNameInput - Styled input for area name in header
 */
const AreaNameInput = styled(Input)`
  && {
    font-weight: 500;
    font-size: 0.875rem;
    background: transparent;
    border: none;
    padding: 0;
    max-width: 16rem;

    &:hover,
    &:focus {
      background: ${({ theme }) => theme.forecast.cardBackground};
      border-radius: 0.25rem;
      padding: 0.125rem 0.5rem;
      margin: -0.125rem -0.5rem;
    }
  }
`

/**
 * DeleteButton - Minimal delete button
 */
const DeleteButton = styled(Button)`
  && {
    color: ${({ theme }) => theme.forecast.tertiaryText};
    padding: 0.25rem;
    height: auto;
    width: auto;

    &:hover {
      color: ${({ theme }) => theme.forecast.danger};
      background: ${({ theme }) => theme.forecast.redGlassBackground};
    }
  }
`

/**
 * AddButton - Dashed button for adding new items
 */
const AddButton = styled(Button)`
  && {
    width: 100%;
    border-style: dashed;
    color: ${({ theme }) => theme.forecast.secondaryText};
    background: transparent;

    &:hover {
      color: ${({ theme }) => theme.forecast.primary};
      border-color: ${({ theme }) => theme.forecast.primary};
      background: ${({ theme }) => theme.forecast.blueGlassBackground};
    }
  }
`

/**
 * ButtonGroup - Container for action buttons
 */
const ButtonGroup = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
`

/**
 * StyledTextArea - Enhanced textarea styling
 */
const StyledTextArea = styled(Input.TextArea)`
  && {
    font-size: 0.875rem;
    line-height: 1.6;
    border-radius: 0.375rem;

    &.ant-input-status-warning {
      border-color: ${({ theme }) => theme.forecast.warning};
    }
  }
`

/**
 * DisclaimerTextArea - Muted textarea for disclaimer
 */
const DisclaimerTextArea = styled(Input.TextArea)`
  && {
    font-size: 0.8125rem;
    line-height: 1.5;
    border-radius: 0.375rem;
    resize: vertical;

    &::placeholder {
      color: ${({ theme }) => theme.forecast.tertiaryText};
    }
  }
`

/**
 * StyledCollapse - Customized collapse for weld section
 */
const StyledCollapse = styled(Collapse)`
  && {
    background: transparent;
    border: none;
    margin-bottom: 1rem;

    .ant-collapse-item {
      border: 1px solid ${({ theme }) => theme.forecast.cardDividerBackground};
      border-radius: 0.5rem !important;
      background: ${({ theme }) => theme.forecast.cardBackground};
      overflow: hidden;
    }

    .ant-collapse-header {
      padding: 0.75rem 1rem !important;
      font-size: 0.875rem;
      font-weight: 500;
      color: ${({ theme }) => theme.forecast.activeText};
    }

    .ant-collapse-content {
      border-top: 1px solid
        ${({ theme }) => theme.forecast.cardDividerBackground};
    }

    .ant-collapse-content-box {
      padding: 1rem;
    }
  }
`

const WELD_TYPE_OPTIONS = [
  'Circumferential Welds',
  'Longseam Welds',
  'Nozzle Welds',
] as const

const DEFAULT_DISCLAIMER =
  'Inadequate inspection surface conditions (typically due to factors such as top-side corrosion, significant surface roughness, or poor coating conditions) led to occasional gaps in the collected ultrasonic data.\n\nVarious geometric obstructions restricted access for data collection (e.g., nozzles, manways, stairs, supports, conduit, external repairs).'

interface InspectionSummaryProps {
  initialValues?: InspectionSummaryData
  onChange: (summaryData: InspectionSummaryData) => void
  onGenerate: () => void
  isStale?: boolean
  geometricUnit: GeckoMeasurementUnit
  measurementPrecision: number
  hasPAUT: boolean
}

interface AreaFormData {
  name: string
  minThickness?: GeckoMeasurement
  avgThickness?: GeckoMeasurement
  nominal?: GeckoMeasurement
  scanNumber?: string
}

interface WeldFormData {
  weldType: 'Circumferential Welds' | 'Longseam Welds' | 'Nozzle Welds'
  indicationCount: number
}

interface SummaryFormValues {
  damageRating?: {
    main?: string
    sub?: string
    description?: string
  }
  areas?: AreaFormData[]
  welds?: WeldFormData[]
  summary?: string
  disclaimer?: string
}

export const InspectionSummary: React.FC<InspectionSummaryProps> = ({
  initialValues,
  onChange,
  onGenerate,
  geometricUnit,
  measurementPrecision,
  hasPAUT,
}) => {
  const [form] = Form.useForm()

  const {
    componentModelsByInspection,
    nominalsByInspection,
    indicationsByInspectionId,
    majorDamageRating,
    totalUT,
    scanCount,
    scanArea,
    inspections,
    uniqueScanTypes,
    formData,
    isLoading,
  } = useRautReportOps()
  const [savedSummaryText, setSavedSummaryText] = useState<string>('')
  const summary = Form.useWatch('summary', form)
  const disclaimer = Form.useWatch('disclaimer', form)
  const disclaimerRows = useMemo(() => {
    const lineCount = (disclaimer ?? DEFAULT_DISCLAIMER).split(/\r?\n/).length

    return Math.min(Math.max(lineCount, 4), 10)
  }, [disclaimer])

  const scanCountsByType = useMemo(() => {
    const counts: Map<string, number> = new Map()

    inspections.forEach((inspection) => {
      if (inspection.data.inspection_type) {
        counts.set(
          inspection.data.inspection_type,
          (counts.get(inspection.data.inspection_type) ?? 0) +
            (scanCount.get(inspection.id) ?? 0)
        )
      }
    })

    return counts
  }, [inspections, scanCount])

  const unitStr =
    geometricUnit === GeckoMeasurementUnit.Millimeters ? 'mm' : 'in'

  const createCompleteSummaryData = useCallback(
    (
      values: SummaryFormValues,
      newSummaryText?: string
    ): InspectionSummaryData => {
      const completeAreas =
        values.areas?.filter(
          (area) => area.minThickness && area.avgThickness && area.scanNumber
        ) || []

      const completeWelds = hasPAUT
        ? values.welds?.filter(
            (weld) => weld.weldType && weld.indicationCount >= 0
          ) || []
        : []

      return {
        areas: completeAreas.map((area) => ({
          name: area.name,
          minThickness: area.minThickness!,
          avgThickness: area.avgThickness!,
          scanNumber: area.scanNumber!,
          nominal: area.nominal!,
        })),
        welds: hasPAUT
          ? completeWelds.map((weld) => ({
              weldType: weld.weldType,
              indicationCount: weld.indicationCount,
            }))
          : [],
        summary: newSummaryText || values.summary || '',
        disclaimer: values.disclaimer ?? DEFAULT_DISCLAIMER,
        thicknessUnit: unitStr as 'mm' | 'in',
      }
    },
    [unitStr, hasPAUT]
  )

  const handleFormChange = useCallback(() => {
    const values = form.getFieldsValue()
    const updatedSummaryData = createCompleteSummaryData(values)
    onChange(updatedSummaryData)
  }, [createCompleteSummaryData, form, onChange])

  const cantileverAreas = useMemo((): AreaFormData[] => {
    return Array.from(componentModelsByInspection.entries()).map(
      ([inspectionId, componentModel]) => {
        const nominalValue = nominalsByInspection.get(inspectionId)
        let nominal: GeckoMeasurement | undefined
        if (nominalValue) {
          nominal = 'start' in nominalValue ? nominalValue.start : nominalValue
        }

        const indications = indicationsByInspectionId.get(inspectionId) || []
        let minThickness: GeckoMeasurement | undefined
        let minScanNumber: string | undefined
        let avgThickness: GeckoMeasurement | undefined
        const roundedVal = geometricUnit.name === 'mm' ? 1 : 3

        indications.forEach((indication) => {
          const minimum = indication.customInfo.stats.minimum
          if (!minThickness || minimum.lt(minThickness)) {
            minThickness = minimum.convertTo(geometricUnit).round(roundedVal)
            minScanNumber = indication.title
            avgThickness = indication.customInfo.stats.mean
              .convertTo(geometricUnit)
              .round(roundedVal)
          }
        })

        return {
          name: componentModel.name || 'Unknown Component',
          nominal,
          minThickness,
          avgThickness,
          scanNumber: minScanNumber,
        }
      }
    )
  }, [
    componentModelsByInspection,
    nominalsByInspection,
    indicationsByInspectionId,
    geometricUnit,
  ])

  const handleUseCantileverDefaults = useCallback(() => {
    form.setFieldValue('areas', cantileverAreas)
    handleFormChange()
  }, [cantileverAreas, form, handleFormChange])

  useEffect(() => {
    if (initialValues?.summary && !savedSummaryText) {
      const currentUnit =
        geometricUnit === GeckoMeasurementUnit.Millimeters ? 'mm' : 'in'
      if (initialValues.thicknessUnit === currentUnit) {
        setSavedSummaryText(initialValues.summary.trim())
      }
    }
  }, [initialValues, savedSummaryText, geometricUnit])

  useEffect(() => {
    const currentValues = form.getFieldsValue()
    form.setFieldsValue({ ...currentValues })
  }, [form, geometricUnit])

  const generateSummaryText = useCallback(() => {
    const currentValues = form.getFieldsValue()
    let areasDescription

    if (currentValues.areas && currentValues.areas.length > 0) {
      const areaDescriptions = currentValues.areas
        .filter(
          (area: AreaFormData) =>
            area.minThickness && area.avgThickness && area.scanNumber
        )
        .map((area: AreaFormData) => {
          const minThicknessValue = area.minThickness!
          const avgThicknessValue = area.avgThickness!

          return `
      ${area.name}:
      • The minimum thickness detected was ${displayMin(minThicknessValue, geometricUnit, measurementPrecision)} with an average thickness of ${displayMean(avgThicknessValue, geometricUnit, measurementPrecision)}.
      • This represents a ${displayWallLoss(minThicknessValue, area.nominal ?? GeckoMeasurement.Zero(), geometricUnit, measurementPrecision)} wall loss from the nominal thickness of ${displayNominal(area.nominal ?? GeckoMeasurement.Zero(), geometricUnit)}.
      • Refer to Location: S${(area.scanNumber ?? '').replace(/^S/i, '')} in Section 7.0 - Scan Summary Table for further details.`
        })
        .join('\n')

      areasDescription =
        areaDescriptions || 'No complete area information has been defined.'
    } else {
      areasDescription = 'No area information has been defined.'
    }

    const scanCountsDescription = uniqueScanTypes
      .map((scanType) => {
        const shortName =
          TYPE_TO_SHORT_NAME[scanType as keyof typeof TYPE_TO_SHORT_NAME] ||
          scanType.toUpperCase()
        const count = scanCountsByType.get(scanType) ?? 0

        return `${count} ${shortName} scans`
      })
      .join(', ')

    let weldsDescription = ''
    if (hasPAUT && currentValues.welds && currentValues.welds.length > 0) {
      const weldDescriptions = currentValues.welds
        .filter(
          (weld: WeldFormData) => weld.weldType && weld.indicationCount >= 0
        )
        .map((weld: WeldFormData) => {
          const indicationLine =
            weld.indicationCount === 0
              ? '• No weld flaw indications were observed at the time of inspection.'
              : `• ${weld.indicationCount} manufacturing-related weld flaw indications were observed at the time of inspection.`

          return `${weld.weldType}
• Analysis of the PAUT data revealed ${weld.indicationCount} indications.
${indicationLine}`
        })
        .join('\n\n')

      if (weldDescriptions) {
        weldsDescription = `\n\n${weldDescriptions}`
      }
    }

    return `A total of ${scanCountsDescription} were performed on ${formData.mainInfo.asset ?? 'Unknown Asset'}. A total of ${totalUT.toLocaleString()} UT readings were collected with coverage of ${(scanArea * 100).toFixed(2)}% of the asset.

${
  majorDamageRating
    ? `Based on analysis of the collected data and in accordance with the Gecko Robotics Damage Rating Assessment Table (GR-DRA), the following damage rating has been assigned to ${formData.mainInfo.asset ?? 'Unknown Asset'}:\n\n${SUB_DRA_MAPPINGS[majorDamageRating]} (${majorDamageRating}): ${getDRADescription(majorDamageRating)}`
    : 'No damage rating has been specified.'
}
${areasDescription}${weldsDescription}`
  }, [
    form,
    uniqueScanTypes,
    formData.mainInfo.asset,
    totalUT,
    scanArea,
    majorDamageRating,
    geometricUnit,
    measurementPrecision,
    scanCountsByType,
    hasPAUT,
  ])

  const isStale = useMemo(() => {
    // No stale detection if no summary has been saved/generated yet
    if (!savedSummaryText) return false

    const currentSummary = summary?.trim() ?? ''
    const generatedSummary = generateSummaryText().trim()

    return currentSummary !== generatedSummary
  }, [generateSummaryText, savedSummaryText, summary])

  const generateSummary = useCallback(() => {
    const currentValues = form.getFieldsValue()
    const summaryText = generateSummaryText()

    const updatedSummaryData = createCompleteSummaryData(
      currentValues,
      summaryText
    )

    form.setFieldsValue({
      ...currentValues,
      summary: summaryText,
    })
    setSavedSummaryText(summaryText.trim())

    onChange(updatedSummaryData)
    onGenerate()
  }, [
    createCompleteSummaryData,
    form,
    generateSummaryText,
    onChange,
    onGenerate,
  ])

  const handleAddArea = useCallback(() => {
    const currentAreas = form.getFieldValue('areas') || []
    const newArea: AreaFormData = {
      name: `Area ${currentAreas.length + 1}`,
      minThickness: undefined,
      avgThickness: undefined,
      scanNumber: undefined,
    }
    form.setFieldValue('areas', [...currentAreas, newArea])
    handleFormChange()
  }, [form, handleFormChange])

  const handleRemoveArea = useCallback(
    (index: number) => {
      const currentAreas = form.getFieldValue('areas') || []
      const updatedAreas = currentAreas.filter(
        (_: AreaFormData, i: number) => i !== index
      )
      form.setFieldValue('areas', updatedAreas)
      handleFormChange()
    },
    [form, handleFormChange]
  )

  useEffect(() => {
    // If the geometric unit changes, make sure the area measurement rounding is updated
    const currentValues = form.getFieldsValue()
    if (currentValues.areas && currentValues.areas.length > 0) {
      const roundedVal = geometricUnit.name === 'mm' ? 1 : 3

      const updatedAreas = currentValues.areas.map((area: AreaFormData) => ({
        ...area,
        minThickness: area.minThickness
          ? area.minThickness.convertTo(geometricUnit).round(roundedVal)
          : undefined,
        avgThickness: area.avgThickness
          ? area.avgThickness.convertTo(geometricUnit).round(roundedVal)
          : undefined,
      }))

      form.setFieldValue('areas', updatedAreas)
      handleFormChange()
    }
  }, [geometricUnit, form, handleFormChange])

  const handleResetDisclaimer = useCallback(() => {
    form.setFieldValue('disclaimer', DEFAULT_DISCLAIMER)
    handleFormChange()
  }, [form, handleFormChange])

  const handleAddWeld = useCallback(() => {
    const currentWelds = form.getFieldValue('welds') || []
    const newWeld: WeldFormData = {
      weldType: 'Circumferential Welds',
      indicationCount: 0,
    }
    form.setFieldValue('welds', [...currentWelds, newWeld])
    handleFormChange()
  }, [form, handleFormChange])

  const handleRemoveWeld = useCallback(
    (index: number) => {
      const currentWelds = form.getFieldValue('welds') || []
      const updatedWelds = currentWelds.filter(
        (_: WeldFormData, i: number) => i !== index
      )
      form.setFieldValue('welds', updatedWelds)
      handleFormChange()
    },
    [form, handleFormChange]
  )

  if (isLoading) return null

  return (
    <SummaryContainer>
      <StyledForm
        form={form}
        onValuesChange={handleFormChange}
        layout='vertical'
        initialValues={{
          ...initialValues,
          areas: initialValues?.areas?.length
            ? initialValues.areas
            : cantileverAreas,
          welds: initialValues?.welds ?? [],
          disclaimer: initialValues?.disclaimer ?? DEFAULT_DISCLAIMER,
        }}>
        {/* Area Details Section */}
        <div>
          <FormSectionTitle>Area Details</FormSectionTitle>
          <FormSectionSubtitle>
            Add inspection areas with thickness measurements. These details will
            be included in the generated summary.
          </FormSectionSubtitle>

          <Form.List name='areas'>
            {(fields) => (
              <>
                {fields.map((field, index) => (
                  <AreaCard key={field.key}>
                    <AreaCardHeader>
                      <Form.Item
                        name={[field.name, 'name']}
                        noStyle
                        rules={[
                          { required: true, message: 'Area name is required' },
                        ]}>
                        <AreaNameInput
                          placeholder='Area name'
                          variant='borderless'
                        />
                      </Form.Item>
                      <DeleteButton
                        type='text'
                        icon={<DeleteOutlined />}
                        onClick={() => handleRemoveArea(index)}
                      />
                    </AreaCardHeader>
                    <AreaCardBody>
                      <Row gutter={[12, 0]}>
                        <Col xs={12} sm={6}>
                          <Form.Item
                            name={[field.name, 'minThickness']}
                            label={`Min (${unitStr})`}
                            rules={[{ required: true }]}
                            normalize={(value: number | undefined) =>
                              value
                                ? GeckoMeasurement.CreateFromValue(
                                    value,
                                    geometricUnit
                                  )
                                : undefined
                            }
                            getValueProps={(
                              value: GeckoMeasurement | undefined
                            ) => ({
                              value: value
                                ? value.toNumber(geometricUnit)
                                : undefined,
                            })}>
                            <InputNumber
                              min={0}
                              step={0.1}
                              style={{ width: '100%' }}
                              placeholder='0.00'
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6}>
                          <Form.Item
                            name={[field.name, 'avgThickness']}
                            label={`Avg (${unitStr})`}
                            rules={[{ required: true }]}
                            normalize={(value: number | undefined) =>
                              value
                                ? GeckoMeasurement.CreateFromValue(
                                    value,
                                    geometricUnit
                                  )
                                : undefined
                            }
                            getValueProps={(
                              value: GeckoMeasurement | undefined
                            ) => ({
                              value: value
                                ? value.toNumber(geometricUnit)
                                : undefined,
                            })}>
                            <InputNumber
                              min={0}
                              step={0.1}
                              style={{ width: '100%' }}
                              placeholder='0.00'
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6}>
                          <Form.Item
                            name={[field.name, 'nominal']}
                            label={`Nominal (${unitStr})`}
                            rules={[{ required: true }]}
                            normalize={(value: number | undefined) =>
                              value
                                ? GeckoMeasurement.CreateFromValue(
                                    value,
                                    geometricUnit
                                  )
                                : undefined
                            }
                            getValueProps={(
                              value: GeckoMeasurement | undefined
                            ) => ({
                              value: value
                                ? value.toNumber(geometricUnit)
                                : undefined,
                            })}>
                            <InputNumber
                              min={0}
                              step={0.1}
                              style={{ width: '100%' }}
                              placeholder='0.00'
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={12} sm={6}>
                          <Form.Item
                            name={[field.name, 'scanNumber']}
                            label='Scan #'
                            rules={[{ required: true }]}>
                            <Input style={{ width: '100%' }} placeholder='S1' />
                          </Form.Item>
                        </Col>
                      </Row>
                    </AreaCardBody>
                  </AreaCard>
                ))}
                <ButtonGroup>
                  <AddButton
                    onClick={handleAddArea}
                    icon={<PlusOutlined />}
                    style={{ flex: 1 }}>
                    Add Area
                  </AddButton>
                  <SecondaryButton
                    onClick={handleUseCantileverDefaults}
                    style={{ flex: 1 }}>
                    Use Cantilever Defaults
                  </SecondaryButton>
                </ButtonGroup>
              </>
            )}
          </Form.List>
        </div>

        {/* Weld Inspection Section - Only shown if PAUT is included */}
        {hasPAUT && (
          <>
            <SectionDivider />
            <div>
              <FormSectionTitle>Weld Inspection (PAUT)</FormSectionTitle>
              <FormSectionSubtitle>
                Add weld inspection findings from PAUT analysis.
              </FormSectionSubtitle>

              <Form.List name='welds'>
                {(fields) => (
                  <>
                    {fields.length > 0 && (
                      <StyledCollapse
                        defaultActiveKey={fields.map((f) => f.key)}
                        items={fields.map((field, index) => ({
                          key: field.key,
                          label: (
                            <Space>
                              <span>Weld Type {index + 1}</span>
                            </Space>
                          ),
                          extra: (
                            <DeleteButton
                              type='text'
                              icon={<DeleteOutlined />}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleRemoveWeld(index)
                              }}
                            />
                          ),
                          children: (
                            <Row gutter={16}>
                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name={[field.name, 'weldType']}
                                  label='Weld Type'
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Weld type is required',
                                    },
                                  ]}>
                                  <Select
                                    options={WELD_TYPE_OPTIONS.map((type) => ({
                                      label: type,
                                      value: type,
                                    }))}
                                    placeholder='Select weld type'
                                  />
                                </Form.Item>
                              </Col>
                              <Col xs={24} sm={12}>
                                <Form.Item
                                  name={[field.name, 'indicationCount']}
                                  label='Indications Found'
                                  rules={[
                                    {
                                      required: true,
                                      message: 'Indication count is required',
                                    },
                                  ]}>
                                  <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    placeholder='0'
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          ),
                        }))}
                      />
                    )}
                    <AddButton onClick={handleAddWeld} icon={<PlusOutlined />}>
                      Add Weld Type
                    </AddButton>
                  </>
                )}
              </Form.List>
            </div>
          </>
        )}

        {/* Generated Summary Section */}
        <SectionDivider />
        <div>
          <FormSectionTitle>Generated Summary</FormSectionTitle>
          <FormSectionSubtitle>
            Review and edit the generated inspection summary text.
          </FormSectionSubtitle>
          <Form.Item name='summary' style={{ marginBottom: '0.75rem' }}>
            <StyledTextArea
              rows={10}
              status={isStale ? 'warning' : undefined}
              placeholder='Click "Generate Summary" to create summary text based on the area details above, or enter custom text here.'
            />
          </Form.Item>
          <ButtonGroup>
            <GenerateButton
              onClick={generateSummary}
              icon={isStale ? <WarningOutlined /> : undefined}>
              {isStale
                ? 'Regenerate Summary (Values Changed)'
                : 'Generate Summary'}
            </GenerateButton>
          </ButtonGroup>
        </div>

        {/* Disclaimer Section */}
        <SectionDivider />
        <div>
          <FormSectionTitle>Inspection Notes</FormSectionTitle>
          <FormSectionSubtitle>
            Edit the inspection notes included in the summary, or restore the
            standard wording.
          </FormSectionSubtitle>
          <Form.Item name='disclaimer' style={{ marginBottom: '0.75rem' }}>
            <DisclaimerTextArea rows={disclaimerRows} />
          </Form.Item>
          <ButtonGroup>
            <SecondaryButton onClick={handleResetDisclaimer}>
              Reset to Default
            </SecondaryButton>
          </ButtonGroup>
        </div>
      </StyledForm>
    </SummaryContainer>
  )
}
